// Press Schedule Gantt — named config constants.
// Ported from the design prototype (design_handoff_press_schedule_gantt). The
// spec is explicit that thresholds must be named config, not inline literals.

export type GanttView = 'day' | '3day' | 'week';

// --- attention thresholds ---------------------------------------------------
// Master switch for the attention engine. OFF: overlap / overbooked were firing
// on things a planner can't or needn't act on (a live overrun pushing into the
// next run reads as "double-booked"; a lane past 20:00 is routine), so the noise
// cost more than the signal. The panel and its toolbar pill stay in place —
// flip this back on once there are rules worth acting on. See ganttAnalyze.ts.
export const ATTENTION_CHECKS_ENABLED = false;
export const SHIP_HOUR = 18;            // jobs assumed to ship 18:00 on their due day
// The plant runs a 06:00–18:00 shift. These bound the shaded "on shift" span on
// the board and the overbooked-lane check — keep them the single source for both
// so the shading can never drift from the rule it's meant to show.
export const SHIFT_START_MIN = 6 * 60;  // 06:00 — shift starts
export const SHIFT_END_MIN = 18 * 60;   // 18:00 — work past this = overbooked lane
export const MFG_TOLERANCE_PCT = 5;     // ±5% planned-vs-actual = "on plan" (green)

// --- interaction ------------------------------------------------------------
export const SNAP_MIN = 15;             // drag-drop snaps to 15-min increments
export const AUTO_SCROLL_NOW_OFFSET_PX = 260; // place NOW this far from the left on load/zoom
export const DRAG_THRESHOLD_PX = 4;     // pointer move beyond this on a bar = drag (else click)
export const MIN_RUN_MIN = 15;          // a run can't be resized shorter than this
// Dead zone around a bar's midpoint before the insert slot flips, in px at the
// current zoom. Stops the gap chattering between two slots when the cursor
// rests on a boundary — a flickering preview reads as an unsure board.
export const INSERT_HYSTERESIS_PX = 14;
// Gap open/close motion. Long enough to read as "making room", short enough not
// to lag the cursor. The per-bar stagger makes the direction of the push legible.
export const GAP_ANIM_MS = 180;
export const GAP_STAGGER_MS = 18;
export const GAP_STAGGER_MAX_MS = 90;
// Safety net: how long the post-drop "placing" ghost may hold the gap open if
// the refetch never confirms the move (offline, API error).
export const PLACING_TIMEOUT_MS = 12_000;

// --- frozen zone (prep-lead demonstration) ----------------------------------
// The operating rhythm we're building toward: at 06:00 each morning, ink
// kitting and CtP plate imaging kick off for the production day that starts
// 24 hours later (06:00→06:00). So at any moment the board has three zones:
//   LOCKED  — today's production day; its kits/plates were prepped yesterday
//   FROZEN  — tomorrow's production day; prep is running for it RIGHT NOW
//   OPEN    — beyond the freeze horizon; plan freely
// None of the prep mechanisms are wired up yet — this overlay is deliberately
// visual-only, to get the team used to reading the schedule this way before
// the kitting/CtP drivers make it real. Flip off here if it confuses more
// than it teaches. The production-day boundary is SHIFT_START_MIN (06:00).
export const FROZEN_ZONE_ENABLED = true;
// How far ahead of a production day its prep kicks off. One full day, per the
// worked example: Tue 06:00 starts prep for Wed 06:00 → Thu 06:00.
export const PREP_LEAD_MIN = 1440;
// Frozen-zone livery. Sky, not the royal blue of one-off downtime blocks —
// "press is out" (striped royal) and "schedule is frozen" (sky wash) must
// read apart at a glance.
export const FREEZE = {
  ink: '#0284c7',                     // lines, flags, labels
  lockedWash: 'rgba(2,132,199,0.045)', // today's day — quiet, it's already fact
  prepWash: 'rgba(2,132,199,0.10)',    // tomorrow's day — the zone being taught
  nextWash: 'rgba(2,132,199,0.02)',    // day after — next in line, barely there
};

// --- press run sheet --------------------------------------------------------
// Horizon presets for the run sheet: limit the prep list to jobs STARTING
// within the next N hours of now. The picker also offers "All" (no cutoff),
// which is the default — a horizon is opt-in per print.
export const RUN_SHEET_HORIZON_HRS = [24, 48, 96];

// --- ink colour filter ------------------------------------------------------
// Click a swatch on a Ready-to-Run card → everything NOT carrying that ink
// dims (dock cards and scheduled bars). Matching is by ink NAME, so the same
// Pantone on two different artworks lights up both.
export interface InkFilter { key: string; name: string; hex: string }
export const inkKey = (s: { name?: string; short: string }): string =>
  (s.name || s.short).trim().toLowerCase();
export const hasInk = (
  seps: { name?: string; short: string }[], key: string,
): boolean => seps.some((s) => inkKey(s) === key);
// How many union swatches a dock card shows before folding into "+n" (the
// 63-artwork gang cards would otherwise draw a 30-swatch strip of mush).
export const MAX_CARD_SWATCHES = 8;

// --- zoom geometry ----------------------------------------------------------
// The timeline is a single continuous canvas: `anchor` is fixed at today 00:00
// and the visible span is governed by a continuous `hourPx` (px per hour) that
// the mouse wheel drives. The Day/3-day/Week buttons are just presets that set
// hourPx. LOGICAL_WINDOW_MIN is the total content span (today → +N days).
export const LOGICAL_WINDOW_MIN = 8 * 1440; // default content span (fallback)
export const MIN_HORIZON_MIN = 14 * 1440;   // canvas always spans at least 14 days
export const HORIZON_BUFFER_MIN = 2 * 1440; // headroom past the last scheduled job
// Minimum projected slip (minutes) before a press shows the "behind" banner —
// ignores sub-quarter-hour noise so the banner doesn't flicker between syncs.
export const DRIFT_BANNER_MIN = 15;
// How often the board re-polls the schedule/actuals in the background so a
// long-open board keeps ticking without a manual page refresh. The Prism press
// sync pushes every few minutes; 3 min matches that cadence without hammering
// the API. (React Query pauses this while the tab is hidden and catches up on
// refocus via refetchOnWindowFocus.)
export const LIVE_REFRESH_MS = 180_000;
export const ZOOM_MIN_PX = 5;               // most zoomed-out (px per hour)
export const ZOOM_MAX_PX = 220;             // most zoomed-in
export const ZOOM_DEFAULT_PX = 64;          // initial = old "Day" density
export const ZOOM_WHEEL_SENS = 0.0016;      // exponential wheel sensitivity
// Preset px/hour for the Day / 3-day / Week buttons.
export const ZOOM_PRESETS: Record<GanttView, number> = { day: 64, '3day': 18, week: 8 };
// Below this px/hour the separation strips are hidden by default (hover-only).
export const SEPS_VISIBLE_HOUR_PX = 40;
// At/above this px/hour the axis uses clock labels (6, 7, 8…); below, compact am/pm.
export const DENSE_LABEL_HOUR_PX = 40;

// --- density → sub-row metrics (px) -----------------------------------------
export type Density = 'airy' | 'cozy' | 'compact';
export const DENSITY: Record<Density, { rowH: number; gap: number; padV: number }> = {
  airy:    { rowH: 84, gap: 7, padV: 14 },
  cozy:    { rowH: 66, gap: 6, padV: 11 },
  compact: { rowH: 50, gap: 5, padV: 9 },
};

// --- layout dimensions (px) -------------------------------------------------
export const RAIL_WIDTH = 240;
// Minimum lane height when a press is running — the rail then carries 4 lines
// (name, machine status + job, activity · speed · operator, sheet progress), so
// reserve room or the content overruns into the lane below.
export const RUNNING_RAIL_MIN_H = 112;
// Axis carries the day caps + hour ticks (top 58px) and, when the frozen-zone
// overlay is on, an 18px zone ribbon along its bottom edge (see GanttBoard).
export const AXIS_HEIGHT = FROZEN_ZONE_ENABLED ? 76 : 58;
export const ZONE_RIBBON_H = 18;
export const DOCK_WIDTH = 300;          // legacy sidebar width (unused once dock is bottom)
export const DOCK_HEIGHT = 260;         // Ready-to-Run bottom panel height
export const PARK_LANE_H = 116;         // Parking lane height (holds chip rows, scrolls if full)
export const MIN_BAR_PX = 6;

// --- progressive bar content reveal (by bar width / height in px) -----------
/**
 * Finished-good blank size, in the order the board shows it: Prism's IM_WIDE
 * first, then IM_LEN. Requested by the pressroom 2026-07-31.
 *
 * DISPLAY ORDER ONLY — the sync mapping and the DB columns are untouched, so
 * production.jobs.length is still IM_LEN. Worth knowing why they read backwards:
 * Prism's two fields are inverted relative to how we name a SHEET (750X1050 is
 * width x length, short side first). For a blank, IM_LEN is the across-the-sheet
 * dimension and IM_WIDE runs down the sheet — confirmed by number-up arithmetic
 * (job 283463: 710/95.5 = 7 across, 1000/327 = 3 down, 7x3 = 21 = its num-up).
 * So this reads IM_WIDE x IM_LEN, i.e. down-sheet x across-sheet.
 */
export const fmtDims = (length: number | null, width: number | null): string | null =>
  length != null && width != null ? `${Math.round(width)}×${Math.round(length)}` : null;

// Coating worth showing: Prism sends 'NONE' (and blanks for unset codes), which
// is noise on a bar or a card — only an actual coating earns the space.
export const hasCoating = (c: string | null | undefined): c is string =>
  !!c && c !== 'NONE';

export const BAR_REVEAL = {
  job:   { w: 34,  h: 30 },
  cust:  { w: 84,  h: 0 },
  desc:  { w: 158, h: 56 },
  meta:  { w: 196, h: 74 },
  check: { w: 92,  h: 0 },
  due:   { w: 50,  h: 0 },
  seps:  { w: 64,  h: 40 },
  warn:  { w: 40,  h: 0 },
};

// --- separation ink reference (process colours) -----------------------------
// BLACK IS 'B', NOT THE CMYK 'K' (house convention, 2026-07-15). `name` stays
// the English word; `short` is the code we print on badges and ink keys.
export const INK = {
  B: { name: 'Black', short: 'B', hex: '#1c1b1a', role: 'process' as const },
  C: { name: 'Cyan', short: 'C', hex: '#009fda', role: 'process' as const },
  M: { name: 'Magenta', short: 'M', hex: '#e5007e', role: 'process' as const },
  Y: { name: 'Yellow', short: 'Y', hex: '#ffd200', role: 'process' as const },
};

// --- press lane order (top → bottom) ----------------------------------------
// Presses render in this explicit order. Any press not listed here falls in
// after the listed ones, alphabetically.
export const PRESS_ORDER = ['P15', 'P14', 'P12', 'P11'];
export function pressRank(code: string): number {
  const i = PRESS_ORDER.indexOf(code);
  return i === -1 ? PRESS_ORDER.length : i;
}

// --- parking lane -----------------------------------------------------------
// The existing system parks jobs onto the 'OTHER' sentinel press_code; the board
// renders that as the "Parking" lane.
export const PARK_CODE = 'OTHER';

// Sentinel press for jobs the scheduler is handling OUTSIDE this system
// ("scheduled for another press / elsewhere"). Like Parking it's a real
// schedule row on a sentinel press_code, so the job drops out of the
// Ready-to-Run backlog but can be restored. Never rendered as a board lane.
export const ELSEWHERE_CODE = 'ELSEWHERE';

// Sentinel drop target: removing a scheduled job from every press/parking lane
// (delete the schedule row) sends its job back to the Ready-to-Run backlog.
export const BACKLOG_CODE = '__BACKLOG__';

// --- Paper vs Cartons backlog split -----------------------------------------
// The dock splits the backlog into two tabs: "Cartons" (finished carton jobs)
// and "Paper" (raw paper/board stock that still runs on-press — parade, etc.).
// A job is "Paper" when its stock_code starts with one of these prefixes.
// 'KL0' (Kaemmerer K Label, e.g. KL070-750X1050F) is deliberately NOT plain
// 'KL': that would also swallow every KLAMU* code — Klabin Klamulti carton
// board, ~200 jobs — and drop them on the Paper tab.
export const PAPER_STOCK_PREFIXES = ['PAR', 'UNI', 'LAB', 'NMP', 'HAO', 'KL0'];
export function isPaperStock(stockCode: string | null | undefined): boolean {
  if (!stockCode) return false;
  const head = stockCode.trim().toUpperCase();
  return PAPER_STOCK_PREFIXES.some((p) => head.startsWith(p));
}

// --- backlog family estimates (no per-job standard resolved in the pile) ----
export const SHEETS_PER_MIN = 220;      // run-rate estimate for backlog dur (~prototype)
export const DEFAULT_SETUP_MIN = 35;    // when a family has no resolved standard
export const DEFAULT_WASHUP_MIN = 20;

// --- design tokens (mapped from gantt.css; used via sx) ----------------------
export const ABARIS = '#CB6015';
export const TOKENS = {
  accent: ABARIS,
  accentStrong: '#9c490d',
  bg: '#fbfaf8',
  panel: '#ffffff',
  ink: 'rgba(38,32,27,0.92)',
  ink2: 'rgba(38,32,27,0.62)',
  ink3: 'rgba(38,32,27,0.45)',
  line: 'rgba(38,32,27,0.13)',
  lineSoft: 'rgba(38,32,27,0.07)',
  good: '#16a34a',
  warn: '#f59e0b',
  bad: '#dc2626',
  shadowSm: '0 1px 2px rgba(35,28,20,.05)',
  shadowMd: '0 16px 40px -12px rgba(35,28,20,.22), 0 3px 8px rgba(35,28,20,.06)',
  radiusBar: 10,
  radiusChip: 8,
  monoFont: '"SF Mono","JetBrains Mono",ui-monospace,Menlo,Consolas,monospace',
};
